import React, { useState } from 'react';
import { useLMS } from '../../context/LMSContext';
import { externalResources as defaultResources } from '../../data/lmsData';

const Sidebar = ({ isOpen, onClose }) => {
  const { 
    currentUser, 
    currentView, 
    setCurrentView, 
    selectedCohortId,
    setSelectedCohortId,
    selectedWeek,
    setSelectedWeek,
    logout,
    isDarkMode,
    toggleDarkMode,
    cohorts,
    getModulesForCohort,
    getVisibleModulesForCohort,
    getCohortById,
    // Resource management
    externalResources,
    addExternalResource,
    updateExternalResource,
    deleteExternalResource,
    // Progress tracking
    getModuleProgress,
    isModuleComplete,
    // Messages
    unreadMessageCount
  } = useLMS();

  const [expandedSections, setExpandedSections] = useState({
    cohorts: true,
    modules: true,
    resources: true
  });

  const [showAddResourceModal, setShowAddResourceModal] = useState(false);
  const [showEditResourceModal, setShowEditResourceModal] = useState(false);
  const [editingResource, setEditingResource] = useState(null);
  const [newResource, setNewResource] = useState({ title: '', url: '', icon: '🔗' });

  const isAdmin = currentUser?.role === 'admin';
  const isProfessor = currentUser?.role === 'professor';
  const isStaff = isAdmin || isProfessor;
  // Clerical role — admin assistant with roster/attendance/password-reset
  // permissions only. NOT part of isStaff so they never get edit access
  // to modules, master modules, cohorts, or other high-level settings.
  const isClerical = currentUser?.role === 'clerical';
  const canViewRosterTools = isStaff || isClerical;
  const selectedCohort = selectedCohortId ? getCohortById(selectedCohortId) : null;
  
  // Use context resources if available, otherwise fall back to defaults
  const resources = externalResources || defaultResources;
  
  // Get modules - staff sees all, students/clerical see only visible ones.
  // Clerical explicitly falls through to the student-visibility path so they
  // can't accidentally see or interact with hidden/unpublished modules.
  const modules = selectedCohortId
    ? (isStaff ? getModulesForCohort(selectedCohortId) : getVisibleModulesForCohort(selectedCohortId))
    : [];

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleNavClick = (view, cohortId = null, week = null) => {
    setCurrentView(view);
    if (cohortId !== null) setSelectedCohortId(cohortId);
    if (week !== null) setSelectedWeek(week);
    if (onClose) onClose(); // Close sidebar on mobile
  };

  const handleCohortClick = (cohortId) => {
    setSelectedCohortId(cohortId);
    setSelectedWeek(null);
    setCurrentView('cohort');
    if (onClose) onClose(); // Close sidebar on mobile
  };

  const handleModuleClick = (weekNumber) => {
    setSelectedWeek(weekNumber);
    setCurrentView('module');
    if (onClose) onClose(); // Close sidebar on mobile
  };

  const handleAddResource = (e) => {
    e.preventDefault();
    if (addExternalResource) {
      addExternalResource({
        title: newResource.title,
        url: newResource.url.startsWith('http') ? newResource.url : `https://${newResource.url}`,
        icon: newResource.icon
      });
    }
    setShowAddResourceModal(false);
    setNewResource({ title: '', url: '', icon: '🔗' });
  };

  const handleDeleteResource = (resourceId, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (deleteExternalResource && window.confirm('Delete this resource?')) {
      deleteExternalResource(resourceId);
    }
  };

  const handleEditResource = (resource, e) => {
    e.preventDefault();
    e.stopPropagation();
    setEditingResource({
      id: resource.id,
      title: resource.title,
      url: resource.url,
      icon: resource.icon || '🔗'
    });
    setShowEditResourceModal(true);
  };

  const handleSaveEditResource = async (e) => {
    e.preventDefault();
    if (updateExternalResource && editingResource) {
      await updateExternalResource(editingResource.id, {
        title: editingResource.title,
        url: editingResource.url.startsWith('http') ? editingResource.url : `https://${editingResource.url}`,
        icon: editingResource.icon
      });
    }
    setShowEditResourceModal(false);
    setEditingResource(null);
  };

  const handleSignOut = () => {
    if (window.confirm('Are you sure you want to sign out?')) {
      logout();
    }
  };

  const iconOptions = ['🔗', '📚', '🎬', '💬', '📝', '🌐', '📖', '🎓', '💡', '⭐'];

  return (
    <aside className={`sidebar ${isOpen ? 'open' : ''}`}>
      {/* Logo */}
      <div className="sidebar-logo">
        <img 
          src="https://cdn.shopify.com/s/files/1/0659/6208/2392/files/codebreaker.png?v=1768442571" 
          alt="Code Breakers NCLEX"
        />
      </div>

      {/* Main Navigation */}
      <nav className="sidebar-nav">
        <div className="nav-section">
          <div className="nav-section-title">Main Menu</div>
          
          <div 
            className={`nav-item ${currentView === 'dashboard' ? 'active' : ''}`}
            onClick={() => handleNavClick('dashboard')}
          >
            <span className="nav-icon">🏠</span>
            <span>Dashboard</span>
          </div>
          
          <div 
            className={`nav-item ${currentView === 'calendar' ? 'active' : ''}`}
            onClick={() => handleNavClick('calendar')}
          >
            <span className="nav-icon">📅</span>
            <span>Calendar</span>
          </div>
          
          {isStaff && (
            <div
              className={`nav-item ${currentView === 'master-modules' ? 'active' : ''}`}
              onClick={() => handleNavClick('master-modules')}
            >
              <span className="nav-icon">📚</span>
              <span>Master Modules</span>
            </div>
          )}

          {(isAdmin || isClerical) && (
            <div
              className={`nav-item ${currentView === 'users' ? 'active' : ''}`}
              onClick={() => handleNavClick('users')}
            >
              <span className="nav-icon">👥</span>
              <span>Manage Users</span>
            </div>
          )}

          {isStaff && (
            <div
              className={`nav-item ${currentView === 'student-progress' ? 'active' : ''}`}
              onClick={() => handleNavClick('student-progress')}
            >
              <span className="nav-icon">📊</span>
              <span>Student Progress</span>
            </div>
          )}

          {isStaff && (
            <div
              className={`nav-item ${currentView === 'assignments' ? 'active' : ''}`}
              onClick={() => handleNavClick('assignments')}
            >
              <span className="nav-icon">📋</span>
              <span>Assignments</span>
            </div>
          )}

          {canViewRosterTools && (
            <div
              className={`nav-item ${currentView === 'attendance' ? 'active' : ''}`}
              onClick={() => handleNavClick('attendance')}
            >
              <span className="nav-icon">📇</span>
              <span>Attendance</span>
            </div>
          )}

          {isStaff && (
            <div
              className={`nav-item ${currentView === 'reflections' ? 'active' : ''}`}
              onClick={() => handleNavClick('reflections')}
            >
              <span className="nav-icon">💭</span>
              <span>Reflections</span>
            </div>
          )}

          {canViewRosterTools && (
            <div
              className={`nav-item ${currentView === 'documents' ? 'active' : ''}`}
              onClick={() => handleNavClick('documents')}
            >
              <span className="nav-icon">📁</span>
              <span>Documents</span>
            </div>
          )}

          {canViewRosterTools && (
            <div
              className={`nav-item ${currentView === 'zoom-settings' ? 'active' : ''}`}
              onClick={() => handleNavClick('zoom-settings')}
            >
              <span className="nav-icon">🎥</span>
              <span>Zoom Settings</span>
            </div>
          )}

          {isAdmin && (
            <div
              className={`nav-item ${currentView === 'thinkific' ? 'active' : ''}`}
              onClick={() => handleNavClick('thinkific')}
            >
              <span className="nav-icon">🎓</span>
              <span>NCLEX Course Access</span>
            </div>
          )}

          {canViewRosterTools && (
            <div
              className={`nav-item ${currentView === 'messages' ? 'active' : ''}`}
              onClick={() => handleNavClick('messages')}
            >
              <span className="nav-icon">✉️</span>
              <span>Messages</span>
              {unreadMessageCount > 0 && (
                <span className="nav-badge">{unreadMessageCount}</span>
              )}
            </div>
          )}

          {canViewRosterTools && (
            <div
              className={`nav-item ${currentView === 'profile' ? 'active' : ''}`}
              onClick={() => handleNavClick('profile')}
            >
              <span className="nav-icon">👤</span>
              <span>My Profile</span>
            </div>
          )}
        </div>

        {/* Cohorts Section - Admin sees cohorts list, Students see their weeks */}
        <div className="nav-section">
          <div
            className="nav-section-title expandable"
            onClick={() => toggleSection('cohorts')}
          >
            <span>{canViewRosterTools ? 'Cohorts' : 'My Course'}</span>
            <span className={`expand-arrow ${expandedSections.cohorts ? 'expanded' : ''}`}>▶</span>
          </div>

          {expandedSections.cohorts && (
            <div className="nav-items">
              {canViewRosterTools ? (
                // Admin/Professor/Clerical see all cohorts (read-only for clerical)
                cohorts.map(cohort => (
                  <div 
                    key={cohort.id}
                    className={`nav-item ${selectedCohortId === cohort.id && currentView === 'cohort' ? 'active' : ''}`}
                    onClick={() => handleCohortClick(cohort.id)}
                  >
                    <span className="nav-icon">📚</span>
                    <div className="nav-item-content">
                      <span className="nav-item-text">{cohort.name}</span>
                      <span className={`status-badge ${cohort.status}`}>
                        {cohort.status}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                // Student sees their weeks/modules with completion status
                <>
                  {/* Dashboard link */}
                  <div 
                    className={`nav-item ${currentView === 'dashboard' ? 'active' : ''}`}
                    onClick={() => handleNavClick('dashboard')}
                  >
                    <span className="nav-icon">🏠</span>
                    <span>Overview</span>
                  </div>
                  
                  {/* Messages link */}
                  <div 
                    className={`nav-item ${currentView === 'messages' ? 'active' : ''}`}
                    onClick={() => handleNavClick('messages')}
                  >
                    <span className="nav-icon">✉️</span>
                    <span>Messages</span>
                    {unreadMessageCount > 0 && (
                      <span className="nav-badge">{unreadMessageCount}</span>
                    )}
                  </div>
                  
                  {/* Profile link */}
                  <div 
                    className={`nav-item ${currentView === 'profile' ? 'active' : ''}`}
                    onClick={() => handleNavClick('profile')}
                  >
                    <span className="nav-icon">👤</span>
                    <span>My Profile</span>
                  </div>
                  
                  {/* Syllabus link */}
                  <div 
                    className={`nav-item ${currentView === 'syllabus' ? 'active' : ''}`}
                    onClick={() => handleNavClick('syllabus')}
                  >
                    <span className="nav-icon">📋</span>
                    <span>Syllabus</span>
                  </div>
                  
                  {/* Documents link */}
                  <div 
                    className={`nav-item ${currentView === 'documents' ? 'active' : ''}`}
                    onClick={() => handleNavClick('documents')}
                  >
                    <span className="nav-icon">📁</span>
                    <span>Documents</span>
                  </div>
                  
                  {/* Student's visible modules/weeks */}
                  {modules.map(module => {
                    const weekNumber = module.weekNumber || module.week_number;
                    const progress = getModuleProgress ? getModuleProgress(weekNumber) : { percentage: 0 };
                    const isComplete = isModuleComplete ? isModuleComplete(weekNumber) : false;
                    
                    return (
                      <div 
                        key={module.id || weekNumber}
                        className={`nav-item ${currentView === 'module' && selectedWeek === weekNumber ? 'active' : ''}`}
                        onClick={() => handleModuleClick(weekNumber)}
                      >
                        <span className="nav-icon">
                          {isComplete ? (
                            <span className="completion-check">✓</span>
                          ) : (
                            '📖'
                          )}
                        </span>
                        <div className="nav-item-content">
                          <span className="nav-item-text">Week {weekNumber}: {module.title}</span>
                          {!isComplete && progress.percentage > 0 && (
                            <span className="progress-indicator">{progress.percentage}%</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          )}
        </div>

        {/* Modules Section - Only show for staff when a cohort is selected */}
        {isStaff && selectedCohortId && (
          <div className="nav-section">
            <div 
              className="nav-section-title expandable"
              onClick={() => toggleSection('modules')}
            >
              <span>Modules</span>
              <span className={`expand-arrow ${expandedSections.modules ? 'expanded' : ''}`}>▶</span>
            </div>
            
            {expandedSections.modules && (
              <div className="nav-items">
                <div 
                  className={`nav-item ${currentView === 'syllabus' ? 'active' : ''}`}
                  onClick={() => handleNavClick('syllabus')}
                >
                  <span className="nav-icon">📋</span>
                  <span>Syllabus</span>
                </div>
                
                {modules.map(module => (
                  <div 
                    key={module.weekNumber}
                    className={`nav-item ${currentView === 'module' && selectedWeek === module.weekNumber ? 'active' : ''} ${!module.isVisible && isStaff ? 'hidden-item' : ''}`}
                    onClick={() => handleModuleClick(module.weekNumber)}
                  >
                    <span className="nav-icon">📖</span>
                    <div className="nav-item-content">
                      <span className="nav-item-text">Week {module.weekNumber}</span>
                      {!module.isVisible && isStaff && (
                        <span className="hidden-badge">Hidden</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* External Resources */}
        <div className="nav-section">
          <div 
            className="nav-section-title expandable"
            onClick={() => toggleSection('resources')}
          >
            <span>Resources</span>
            <span className={`expand-arrow ${expandedSections.resources ? 'expanded' : ''}`}>▶</span>
          </div>
          
          {expandedSections.resources && (
            <div className="nav-items">
              {resources.map(resource => (
                <a 
                  key={resource.id}
                  className="nav-item external"
                  href={resource.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span className="nav-icon">{resource.icon}</span>
                  <span>{resource.title}</span>
                  <span className="external-arrow">↗</span>
                  {isStaff && (
                    <div className="resource-actions">
                      <button 
                        className="edit-resource-btn"
                        onClick={(e) => handleEditResource(resource, e)}
                        title="Edit resource"
                      >
                        ✎
                      </button>
                      <button 
                        className="delete-resource-btn"
                        onClick={(e) => handleDeleteResource(resource.id, e)}
                        title="Delete resource"
                      >
                        ×
                      </button>
                    </div>
                  )}
                </a>
              ))}
              
              {isStaff && (
                <div 
                  className="nav-item add-resource"
                  onClick={() => setShowAddResourceModal(true)}
                >
                  <span className="nav-icon">➕</span>
                  <span>Add Resource</span>
                </div>
              )}
            </div>
          )}
        </div>
      </nav>

      {/* Footer */}
      <div className="sidebar-footer">
        {/* Theme Toggle */}
        <div className="theme-toggle" onClick={toggleDarkMode}>
          <span className="nav-icon">{isDarkMode ? '☀️' : '🌙'}</span>
          <span>{isDarkMode ? 'Light Mode' : 'Dark Mode'}</span>
        </div>

        {/* User Profile */}
        <div className="user-profile">
          <div className="user-avatar">
            {currentUser?.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U'}
          </div>
          <div className="user-info">
            <div className="user-name">{currentUser?.name}</div>
            <div className="user-role">{
              currentUser?.role === 'admin' ? 'Administrator'
                : currentUser?.role === 'professor' ? 'Professor'
                : currentUser?.role === 'clerical' ? 'Clerical'
                : 'Student'
            }</div>
          </div>
        </div>

        {/* Sign Out Button */}
        <div className="sign-out-btn" onClick={handleSignOut}>
          <span className="nav-icon">🚪</span>
          <span>Sign Out</span>
        </div>
      </div>

      {/* Add Resource Modal */}
      {showAddResourceModal && (
        <div className="modal-overlay" onClick={() => setShowAddResourceModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add Resource Link</h3>
              <button className="modal-close" onClick={() => setShowAddResourceModal(false)}>×</button>
            </div>
            
            <form onSubmit={handleAddResource}>
              <div className="form-group">
                <label>Icon</label>
                <div className="icon-picker">
                  {iconOptions.map(icon => (
                    <button
                      key={icon}
                      type="button"
                      className={`icon-option ${newResource.icon === icon ? 'selected' : ''}`}
                      onClick={() => setNewResource({...newResource, icon})}
                    >
                      {icon}
                    </button>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Title</label>
                <input 
                  type="text"
                  placeholder="e.g., Study Guide"
                  value={newResource.title}
                  onChange={(e) => setNewResource({...newResource, title: e.target.value})}
                  required
                />
              </div>
              
              <div className="form-group">
                <label>URL</label>
                <input 
                  type="text"
                  placeholder="https://example.com"
                  value={newResource.url}
                  onChange={(e) => setNewResource({...newResource, url: e.target.value})}
                  required
                />
              </div>
              
              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={() => setShowAddResourceModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Add Resource
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Resource Modal */}
      {showEditResourceModal && editingResource && (
        <div className="modal-overlay" onClick={() => setShowEditResourceModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Resource Link</h3>
              <button className="modal-close" onClick={() => setShowEditResourceModal(false)}>×</button>
            </div>
            
            <form onSubmit={handleSaveEditResource}>
              <div className="form-group">
                <label>Icon</label>
                <div className="icon-picker">
                  {iconOptions.map(icon => (
                    <button
                      key={icon}
                      type="button"
                      className={`icon-option ${editingResource.icon === icon ? 'selected' : ''}`}
                      onClick={() => setEditingResource({...editingResource, icon})}
                    >
                      {icon}
                    </button>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Title</label>
                <input 
                  type="text"
                  placeholder="e.g., Study Guide"
                  value={editingResource.title}
                  onChange={(e) => setEditingResource({...editingResource, title: e.target.value})}
                  required
                />
              </div>
              
              <div className="form-group">
                <label>URL</label>
                <input 
                  type="text"
                  placeholder="https://example.com"
                  value={editingResource.url}
                  onChange={(e) => setEditingResource({...editingResource, url: e.target.value})}
                  required
                />
              </div>
              
              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={() => setShowEditResourceModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </aside>
  );
};

export default Sidebar;
